import {
  DishAllergen,
  DishIngredient,
  GetAllResolvedEdiblesResponse,
} from "api-types";

import React from "react";

import { MappedProduct } from "../ProductDrawer/product.types";
import { OptionalAllergenDetail } from "./OptionalAllergenDetail";

type AllergenDetailStatus = "removable" | "partial" | "not_removable";

interface RenderAllergenDetailsParams {
  matchedAllergens: DishAllergen[];
  linkedDish: GetAllResolvedEdiblesResponse["dishes"][string] | undefined;
  optionalIngredientIds?: string[];
  filteredProducts: (MappedProduct & {
    allergens?: DishAllergen[];
    ingredients?: (DishIngredient & { allergens?: DishAllergen[] })[];
    isOptional?: boolean;
  })[];
  onProductClick?: (product: MappedProduct) => void;
}

function getIngredientNames(
  linkedDish: GetAllResolvedEdiblesResponse["dishes"][string] | undefined,
  allergen: DishAllergen,
  optionalIngredientIds: string[] | undefined,
  isOptional: boolean
): string[] {
  if (!linkedDish?.directlyLinkedIngredients) return [];

  return linkedDish.directlyLinkedIngredients
    .filter(
      (ingredient) =>
        ingredient.allergens.some(
          (a) => a.igfdTaxAllergenId === allergen.igfdTaxAllergenId
        ) &&
        (isOptional
          ? optionalIngredientIds?.includes(ingredient.igfdTaxIngredientId)
          : !optionalIngredientIds?.includes(ingredient.igfdTaxIngredientId))
    )
    .map((ingredient) => ingredient.name);
}

export function renderAllergenDetails({
  matchedAllergens,
  linkedDish,
  optionalIngredientIds,
  filteredProducts,
  onProductClick,
}: RenderAllergenDetailsParams): JSX.Element[] {
  return matchedAllergens.map((allergen) => {
    const removableIngredients = getIngredientNames(
      linkedDish,
      allergen,
      optionalIngredientIds,
      true
    );
    const nonRemovableIngredients = getIngredientNames(
      linkedDish,
      allergen,
      optionalIngredientIds,
      false
    );

    const nonRemovableFromProducts =
      filteredProducts
        ?.filter(
          (p) =>
            !p.isOptional &&
            p.allergens?.some(
              (a) => a.igfdTaxAllergenId === allergen.igfdTaxAllergenId
            )
        )
        .flatMap(
          (p) =>
            p.ingredients
              ?.filter((i) =>
                i?.allergens?.some(
                  (a) => a.igfdTaxAllergenId === allergen.igfdTaxAllergenId
                )
              )
              .map((i) => i.ingredientName) ?? []
        ) || [];

    const combinedNonRemovableItems = Array.from(
      new Set([...nonRemovableIngredients, ...nonRemovableFromProducts])
    );

    const hasAllRemovableItems = allergen.isOptional;
    const hasSomeRemovableItems =
      !allergen.isOptional &&
      (removableIngredients.length > 0 ||
        (filteredProducts || []).some(
          (p) =>
            p.isOptional &&
            p.allergens?.some(
              (a) => a.igfdTaxAllergenId === allergen.igfdTaxAllergenId
            )
        ));

    const nonRemovableItems = combinedNonRemovableItems;

    const status: AllergenDetailStatus = hasAllRemovableItems
      ? "removable"
      : hasSomeRemovableItems
      ? "partial"
      : "not_removable";

    return (
      <OptionalAllergenDetail
        key={allergen.igfdTaxAllergenId}
        allergen={allergen}
        status={status}
        removableIngredientNames={removableIngredients}
        removableProducts={(filteredProducts || [])
          .filter(
            (p) =>
              p.isOptional &&
              p.allergens?.some(
                (a) => a.igfdTaxAllergenId === allergen.igfdTaxAllergenId
              )
          )
          .map((p) => ({
            id: p.id,
            name: p.name,
            allergens: p.allergens,
            isOptional: !!p.isOptional,
          }))}
        onProductClick={onProductClick}
        nonRemovableItems={nonRemovableItems}
      />
    );
  });
}
